import { useEffect, useRef, useState } from "react";

export type CameraCaptureModalProps = {
  open: boolean;
  onClose: () => void;
  onCapture: (imageBase64: string) => void;
  // Optional props for customization
  facingMode?: "user" | "environment";
  imageType?: "image/jpeg" | "image/png";
  imageQuality?: number; // 0..1
};

export function CameraCaptureModal({
  open,
  onClose,
  onCapture,
  facingMode = "user",
  imageType = "image/jpeg",
  imageQuality = 0.9,
}: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start/stop camera when modal opens/closes
  useEffect(() => {
    let isMounted = true;

    const startCamera = async () => {
      setError(null);
      if (!open) return;

      // stop previous stream if any
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      setIsStarting(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (!isMounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        console.error("[CameraCaptureModal] Camera access error:", e);
        setError("Unable to access camera. Please allow permissions.");
      } finally {
        if (isMounted) setIsStarting(false);
      }
    };

    if (open) startCamera();

    return () => {
      isMounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, facingMode]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64 = canvas.toDataURL(imageType, imageQuality);
    onCapture(base64);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "#00000091" }}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-card text-card-foreground shadow-lg"
        style={{ backgroundColor: "#fff" }}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-semibold">Capture Photo</h3>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 text-dark rounded-full px-5 py-2 shadow hover:bg-gray-300"
            aria-label="Close camera"
          >
            Close
          </button>
        </div>

        <div className="px-4 py-4">
          {error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : (
            <>
              <div className="aspect-video w-full overflow-hidden rounded-md border bg-black">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-contain cap-video"
                />
              </div>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Ensure your face and ID are clearly visible and well-lit.
              </p>
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 text-dark rounded-full px-5 py-2 shadow hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCapture}
            disabled={isStarting || !!error}
            className="bgPrimaryColor uppercase text-white rounded-full px-5 py-2 text-center md:text-left bg-white hover:bg-blue-800 transform transition duration-500 hover:text-white relative focus:outline-none border-2 border-solid rounded-lg text-xs font-semibold tracking-widest overflow-hidden"
          >
            {isStarting ? "Starting..." : "Capture Photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
